<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Doctors</h1>
          <p>Team physicians and consultants displayed on the website</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <div class="wsp-search">
          <span class="wsp-search__icon">🔍</span>
          <input v-model.trim="search" type="text" placeholder="Search doctors…" />
        </div>
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreate">+ Add Doctor</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <section class="wsp-card">
      <header class="wsp-card__head">
        <h2>All Doctors</h2>
        <span class="wsp-card__count">{{ filtered.length }} doctor(s)</span>
      </header>

      <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
      <div v-else-if="!filtered.length" class="wsp-empty">
        <div class="wsp-empty__title">No doctors found</div>
        <div>Add a doctor to get started.</div>
      </div>
      <div v-else>
        <table class="wsp-table">
          <thead>
            <tr>
              <th style="width:60px;">Photo</th>
              <th>Name</th>
              <th>Position</th>
              <th>Active</th>
              <th class="wsp-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filtered" :key="row.id">
              <td>
                <img v-if="row.image" :src="row.image" class="wsp-thumb wsp-thumb--circle" alt="" />
                <span v-else style="color:#9ca3af;">—</span>
              </td>
              <td>
                <strong>{{ row.name }}</strong>
                <div style="font-size:0.74rem;color:#6b7280;">
                  <span v-if="row.qualifications">{{ row.qualifications }}</span>
                  <span v-if="row.qualifications && row.designation"> · </span>
                  <span v-if="row.designation">{{ row.designation }}</span>
                </div>
              </td>
              <td>{{ row.position == null ? '—' : row.position }}</td>
              <td>
                <label class="wsp-toggle" @click.prevent="toggleActive(row)">
                  <input type="checkbox" :checked="row.is_active" :disabled="patching === row.id" />
                  <span :class="['wsp-pill', row.is_active ? 'wsp-pill--green' : 'wsp-pill--gray']">
                    {{ row.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </label>
              </td>
              <td class="wsp-actions">
                <button class="wsp-icon-btn" @click="openEdit(row)" title="Edit">✎</button>
                <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDelete(row)" title="Delete">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="modalOpen" class="wsp-modal-backdrop" @click.self="closeModal">
      <div class="wsp-modal">
        <header class="wsp-modal__head">
          <h3>{{ form.id ? 'Edit Doctor' : 'Add Doctor' }}</h3>
          <button class="wsp-modal__close" @click="closeModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field wsp-field--full">
              <label>Name <span style="color:#b91c1c">*</span></label>
              <input v-model="form.name" type="text" />
              <span v-if="errors.name" class="wsp-field__error">{{ errors.name }}</span>
            </div>
            <div class="wsp-field">
              <label>Qualifications</label>
              <input v-model="form.qualifications" type="text" placeholder="MBBS, MD" />
            </div>
            <div class="wsp-field">
              <label> Reg. No</label>
              <input v-model="form.designation" type="text" placeholder="123456" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Photo</label>

              <div class="doc-img-row">
                <div class="doc-avatar" :class="{ 'doc-avatar--empty': !form.image }">
                  <img v-if="form.image" :src="form.image" alt="" />
                  <span v-else>No photo</span>
                </div>

                <div class="doc-img-actions">
                  <div
                    class="img-drop"
                    :class="{ 'img-drop--over': dragOver, 'img-drop--busy': uploading }"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="onDrop($event)"
                    @click="$refs.imgInput.click()"
                  >
                    <input
                      ref="imgInput"
                      type="file"
                      accept="image/*"
                      hidden
                      @change="onPickFile"
                    />
                    <div v-if="uploading" class="img-drop__hint">
                      <CSpinner size="sm" /> Uploading…
                    </div>
                    <div v-else class="img-drop__hint">
                      <strong>{{ form.image ? 'Replace photo' : 'Click or drop image' }}</strong>
                      <span>JPG, PNG, WEBP · up to 10 MB</span>
                    </div>
                  </div>
                  <button
                    v-if="form.image"
                    type="button"
                    class="doc-img-remove"
                    @click="form.image = ''"
                  >Remove</button>
                </div>
              </div>
              <span v-if="errors.upload" class="wsp-field__error">{{ errors.upload }}</span>

              <details class="img-url-fallback">
                <summary>Or paste a URL</summary>
                <input v-model="form.image" type="text" placeholder="https://…" />
              </details>
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="form.position" type="number" />
            </div>
            <div class="wsp-field">
              <label>&nbsp;</label>
              <label class="wsp-toggle">
                <input v-model="form.is_active" type="checkbox" /> Active
              </label>
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create doctor') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const empty = () => ({ id: null, name: '', qualifications: '', designation: '', image: '', position: null, is_active: true })

export default {
  name: 'WebsitesDoctors',
  data () {
    return {
      rows: [], loading: false, error: '',
      search: '',
      modalOpen: false, saving: false, patching: null,
      form: empty(), errors: {},
      dragOver: false, uploading: false,
    }
  },
  computed: {
    filtered () {
      const q = this.search.toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.qualifications || '').toLowerCase().includes(q) ||
        (r.designation || '').toLowerCase().includes(q)
      )
    },
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true; this.error = ''
      try { this.rows = await websitesApi.doctorsList() }
      catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },
    openCreate () {
      this.form = empty()
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    openEdit (row) {
      this.form = {
        id: row.id,
        name: row.name || '',
        qualifications: row.qualifications || '',
        designation: row.designation || '',
        image: row.image || '',
        position: row.position == null ? null : Number(row.position),
        is_active: !!row.is_active,
      }
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    resetUploader () { this.dragOver = false; this.uploading = false },
    onPickFile (e) {
      const file = (e.target.files || [])[0]
      e.target.value = ''
      if (file) this.uploadFile(file)
    },
    onDrop (e) {
      this.dragOver = false
      const file = Array.from(e.dataTransfer.files || []).find(f => f.type.startsWith('image/'))
      if (file) this.uploadFile(file)
    },
    async uploadFile (file) {
      if (this.uploading) return
      this.errors = { ...this.errors, upload: '' }
      this.uploading = true
      try {
        const data = await websitesApi.uploadImage(file, 'websites/doctors')
        if (data && data.url) this.form.image = data.url
      } catch (err) {
        this.errors = { ...this.errors, upload: this.extractError(err) }
      } finally {
        this.uploading = false
      }
    },
    closeModal () { if (!this.saving) this.modalOpen = false },
    async toggleActive (row) {
      this.patching = row.id
      try {
        const next = !row.is_active
        const updated = await websitesApi.doctorPatch(row.id, { is_active: next })
        const idx = this.rows.findIndex(r => r.id === row.id)
        if (idx >= 0) this.$set(this.rows, idx, updated)
      } catch (e) { this.error = this.extractError(e) }
      finally { this.patching = null }
    },
    async save () {
      this.errors = {}
      if (!this.form.name.trim()) { this.errors.name = 'Name is required'; return }
      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          qualifications: this.form.qualifications,
          designation: this.form.designation,
          image: this.form.image || null,
          position: this.form.position == null || this.form.position === '' ? null : Number(this.form.position),
          is_active: !!this.form.is_active,
        }
        if (this.form.id) await websitesApi.doctorUpdate(this.form.id, payload)
        else              await websitesApi.doctorCreate(payload)
        this.modalOpen = false
        await this.fetch()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDelete (row) {
      if (!confirm(`Delete doctor "${row.name}"?`)) return
      try { await websitesApi.doctorDelete(row.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },
    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
}
</script>

<style scoped>
.doc-img-row {
  display: flex;
  gap: 14px;
  align-items: stretch;
}
.doc-avatar {
  flex: 0 0 auto;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 1px solid #e5e9f2;
  background: #f3f4f8;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.doc-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.doc-avatar--empty {
  border-style: dashed;
  color: #9ca3af;
  font-size: 0.7rem;
}
.doc-img-actions {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.img-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1.5px dashed #c5cbd6;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  min-height: 70px;
}
.img-drop:hover { border-color: #16a07a; background: #f0fbf6; }
.img-drop--over { border-color: #16a07a; background: #e6f7ef; }
.img-drop--busy { cursor: progress; opacity: 0.85; }
.img-drop__hint {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  color: #4b5563;
}
.img-drop__hint strong { color: #1f2a44; font-weight: 600; }
.img-drop__hint span { font-size: 0.72rem; color: #6b7280; }

.doc-img-remove {
  align-self: flex-start;
  background: transparent;
  border: 1px solid #e5e9f2;
  color: #b91c1c;
  font-size: 0.74rem;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.doc-img-remove:hover { background: #fef2f2; border-color: #fecaca; }

.img-url-fallback {
  margin-top: 10px;
  font-size: 0.78rem;
}
.img-url-fallback summary {
  cursor: pointer;
  color: #6b7280;
  padding: 4px 0;
  user-select: none;
}
.img-url-fallback summary:hover { color: #0e5b46; }
.img-url-fallback input { margin-top: 6px; }
</style>
